import React from "react";
import { OI_EFFECT_TIMEOUT_MS } from "constants/configs";
import {
  clearPreviousQueuedItems,
  selectApplicationDocumentUploadQueuedItems,
  selectPreviousApplicationDocumentUploadQueuedItems,
} from "store/app-state/application-document-upload-queue/applicationDocumentUploadQueue";
import { selectIsIntelligenceGradientVisible, setIsIntelligenceGradientVisible } from "store/app-state/ui/ui";
import { selectDocumentEntitiesByApplicationIdForContainer } from "store/domain-data/document/document";
import { useStoreDispatch, useStoreSelector } from "store/hooks";
import { DocumentUtil } from "models/Document.model";

export const useDocumentProcessingStatus = (args: { documentContainerId: number; applicationId: number }) => {
  const { documentContainerId, applicationId } = args;

  const dispatch = useStoreDispatch();
  const documents = useStoreSelector((state) =>
    selectDocumentEntitiesByApplicationIdForContainer(state, { applicationId, documentContainerId })
  );
  const documentNames = React.useMemo(() => documents.map((document) => document.name), [documents]);

  const queueItems = useStoreSelector(selectApplicationDocumentUploadQueuedItems);
  const queueItemsInContainer = React.useMemo(
    () => queueItems.filter((item) => documentNames.includes(item.documentName)),
    [documentNames, queueItems]
  );

  const previousQueuedItems = useStoreSelector(selectPreviousApplicationDocumentUploadQueuedItems);
  const previousQueuedItemsInContainer = React.useMemo(
    () => previousQueuedItems.filter((item) => documentNames.includes(item.documentName)),
    [documentNames, previousQueuedItems]
  );

  const isDocumentUploadedInCurrentSession = React.useCallback(
    (documentName: string) => {
      if (queueItemsInContainer.some((item) => item.documentName === documentName)) {
        return true;
      }
      return previousQueuedItemsInContainer.some((item) => item.documentName === documentName);
    },
    [previousQueuedItemsInContainer, queueItemsInContainer]
  );

  const hasUnsettledDocumentsUploadedInCurrentSession = documents.some((document) => {
    if (!isDocumentUploadedInCurrentSession(document.name)) {
      return false;
    }
    return !DocumentUtil.isSettled(document);
  });

  // When there is document unsettled in the container, show processing status
  const isProcessingStatusVisible = hasUnsettledDocumentsUploadedInCurrentSession;

  // Detect if there is extracted data for previous uploaded items
  // Note: AI alert will be visible when:
  // - All documents in this round & container are uploaded
  // - Any document in this round & container having extracted data
  const hasExtractedData = React.useMemo(
    () =>
      documents
        .filter((document) => isDocumentUploadedInCurrentSession(document.name))
        .some((document) => document.hasExtractedData),
    [documents, isDocumentUploadedInCurrentSession]
  );

  const isIntelligenceAlertVisible = useStoreSelector(selectIsIntelligenceGradientVisible);

  const showIntelligenceAlert = React.useCallback(() => {
    dispatch(setIsIntelligenceGradientVisible(true));

    setTimeout(() => {
      dispatch(setIsIntelligenceGradientVisible(false));
      dispatch(clearPreviousQueuedItems());
    }, OI_EFFECT_TIMEOUT_MS);
  }, [dispatch]);

  React.useEffect(() => {
    if (hasExtractedData && !hasUnsettledDocumentsUploadedInCurrentSession) {
      showIntelligenceAlert();
    }
  }, [hasExtractedData, hasUnsettledDocumentsUploadedInCurrentSession, showIntelligenceAlert]);

  return {
    isIntelligenceAlertVisible,
    isProcessingStatusVisible,
  };
};
